import * as React from "react";
import {
    Query,
    QueryCondition,
    QueryFilter,
    OverreadStatus,
    SpirometrySessionMetadata,
    OverreadChallengeStatus
} from "../../../misc/api/apiTypes";
import Modal from "../../../misc/components/modal";
import Select from "react-select";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";

export const StatusLabelMember = "StatusId";
export const ChallengeStatusLabelMember = "ChallengeStatusId";
const StatusFilterQueryCondition = QueryCondition.Equal;
export const StatusHeader = "Status";
export const SiteStatusHeader = "Site Status";
export const OverreadStatusHeader = "Over-read Status";

interface StatusFilterProps {
    onApply: (member: string, condition: QueryCondition, values: string[]) => void;
    onClose: () => void;
    query: Query<SpirometrySessionMetadata>;
    hasChallengeStatuses?: boolean;
}

const StatusFilterModal = ({ onClose, onApply, query, hasChallengeStatuses = false }: StatusFilterProps): JSX.Element => {
    const legacyClient = useWebPortalLegacyClient();

    const getQueryFilter = (query: Query<SpirometrySessionMetadata>, challenge = false): QueryFilter => {
        const labelMember = challenge ? ChallengeStatusLabelMember : StatusLabelMember;
        return query.Filters.find(
            filter =>
                filter.Member === labelMember &&
                filter.Condition === StatusFilterQueryCondition
        );
    };

    const handleOverreadChange = (values: any, event?: any) => {
        const updatedValues = values.map(value => value["Id"]);
        const updatedOverreadValues = updatedValues.length > 0 ? updatedValues : [];
        setOverreadValues(updatedOverreadValues);
    };

    const handleChallengeChange = (values: any, event?: any) => {
        const updatedValues = values.map(value => value["Id"]);
        setChallengeValues(updatedValues.length > 0 ? updatedValues : null);
    };

    const handleApply = () => {
        onApply(StatusLabelMember, StatusFilterQueryCondition, overreadValues);
        if (hasChallengeStatuses) {
            onApply(ChallengeStatusLabelMember, StatusFilterQueryCondition, challengeValues);
        }
    };

    const handleClear = (event?: any) => {
        onApply(StatusLabelMember, StatusFilterQueryCondition, []);
        if (hasChallengeStatuses) {
            onApply(ChallengeStatusLabelMember, StatusFilterQueryCondition, []);
        }
    };

    const overreadQueryFilter = getQueryFilter(query) || { Values: [] };
    const challengeQueryFilter = getQueryFilter(query, true) || { Values: [] };

    const [isLoading, setIsLoading] = React.useState(false);
    const [challengeValues, setChallengeValues] = React.useState<string[]>(
        challengeQueryFilter.Values as string[]
    );
    const [overreadValues, setOverreadValues] = React.useState<string[]>(
        overreadQueryFilter.Values as string[]
    );

    const [challengeStatuses, setChallengeStatuses] = React.useState<OverreadChallengeStatus[]>();
    const [overreadStatuses, setOverreadStatuses] = React.useState<OverreadStatus[]>(null);

    React.useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);

            legacyClient.getSessionStatuses().then(statuses => {
                setOverreadStatuses(statuses);
            });

            if (hasChallengeStatuses) {
                legacyClient.getOverreadChallengeStatuses({}).then(query => {
                    setChallengeStatuses(query.Data);
                });
            }

            setIsLoading(false);
        };

        fetchData();
    }, []);

    return (
        <Modal show={true} header={StatusHeader + " Filter"} closeModalHandler={onClose}>
            <div className="modal-body">
                {isLoading ? (
                    <LoadingWithMessage message={"Loading..."} />
                ) : (
                    <>
                        <div className="row">
                            <div className="col-md-12 form-group">
                                <label>Status</label>
                                <Select
                                    options={overreadStatuses}
                                    value={overreadStatuses && overreadValues.length > 0 && overreadStatuses.filter(os => overreadValues.includes(os.Id))}
                                    getOptionValue={o => o.Id}
                                    getOptionLabel={o => o.Name}
                                    isMulti={true}
                                    onChange={handleOverreadChange}
                                />
                            </div>
                        </div>
                        {hasChallengeStatuses ?
                            <div className="row">
                                <div className="col-md-12 form-group">
                                    <label>Challenge Status</label>
                                    <Select
                                        options={challengeStatuses}
                                        value={challengeStatuses && challengeValues.length > 0 && challengeStatuses.filter(os => challengeValues.includes(os.Id))}
                                        getOptionValue={o => o.Id}
                                        getOptionLabel={o => o.Name}
                                        isMulti={true}
                                        onChange={handleChallengeChange}
                                    />
                                </div>
                            </div> : null}
                    </>
                )}
            </div>

            <div className="modal-footer">
                <button disabled={isLoading} className="btn btn-primary" onClick={handleApply}>
                    OK
                </button>
                <button disabled={isLoading} className="btn btn-danger" onClick={handleClear}>
                    Clear
                </button>
            </div>
        </Modal>
    );
};

export default StatusFilterModal;
